import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Bell, BookOpen, Building2, Calendar, ClipboardList, Database, FileText, Gauge, Image, KeyRound, LayoutList, LogOut, MapPin, Mail, Menu, Settings as Cog, Shield, Star, Tags, Users, UserSquare2, BarChart3, Handshake, Megaphone, Bot, Plug, SlidersHorizontal, LayoutTemplate } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Logo } from "../components/Logo";
import { LangToggle } from "../components/Navbar";
import { useI18n } from "../lib/i18n";
import { AuthProvider, aapi, apiErr, can, useA, useAdminApi, useAuth, SA, MG, AG, ED, CONTENT, fmtDT } from "./lib";
import { ResourceList, ResourceEdit } from "./Resources";
import Dashboard from "./Dashboard";
import Leads from "./Leads";
import Appointments from "./Appointments";
import { UsersPage, ActivityPage, BackupsPage, EmailLogPage, SettingsPage, StatsPage } from "./System";
import ErrorBoundary from "../components/ErrorBoundary";
import { applyTheme } from "../lib/theme";
import { ChatsPage, IntegrationsPage, NotifyPrefs, DocsPage } from "./Phase3";
import { HomepagePage } from "./Homepage";

export const NAV = [
  ["dashboard", Gauge, "Dashboard", "لوحة التحكم", [SA, MG]],
  ["leads", ClipboardList, "Leads CRM", "العملاء المحتملون", [SA, MG, AG]],
  ["appointments", Calendar, "Appointments", "المواعيد", [SA, MG, AG]],
  ["properties", Building2, "Properties", "العقارات", [SA, MG, ED, AG]],
  ["projects", LayoutList, "Projects", "المشاريع", CONTENT],
  ["articles", BookOpen, "News & Articles", "الأخبار والمقالات", CONTENT],
  ["banners", Image, "Banners", "البانرات", CONTENT],
  ["testimonials", Star, "Testimonials", "آراء العملاء", CONTENT],
  ["partners", Handshake, "Partners", "الشركاء", CONTENT],
  ["stats", BarChart3, "Company Stats", "إحصاءات الشركة", CONTENT],
  ["homepage", LayoutTemplate, "Homepage", "الصفحة الرئيسية", CONTENT],
  ["pages", FileText, "Static Pages", "الصفحات الثابتة", CONTENT],
  ["categories", Tags, "Categories", "التصنيفات", CONTENT],
  ["locations", MapPin, "Locations", "المواقع", CONTENT],
  ["agents", UserSquare2, "Agents", "الوكلاء", [SA, MG]],
  ["chats", Bot, "AI Chats", "محادثات الذكاء الاصطناعي", [SA, MG]],
  ["docs", BookOpen, "Documentation", "التوثيق", [SA]],
  ["integrations", Plug, "Integrations", "التكاملات", [SA, MG]],
  ["email-log", Mail, "Email Log", "سجل البريد", [SA, MG]],
  ["settings", Cog, "Settings", "الإعدادات", [SA]],
  ["users", Users, "Users & Roles", "المستخدمون والأدوار", [SA]],
  ["activity", Shield, "Activity Log", "سجل النشاط", [SA]],
  ["backups", Database, "Backups", "النسخ الاحتياطية", [SA]],
];
export const ROLE_LABEL = { super_admin: ["Super Admin", "مشرف عام"], manager: ["Manager", "مدير"], sales_agent: ["Sales Agent", "وكيل مبيعات"], content_editor: ["Content Editor", "محرر محتوى"] };

function LoginPage() {
  const a = useA();
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/admin" replace />;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await login(f.email, f.password); nav("/admin"); } catch (ex) { setErr(apiErr(ex)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-login-page" className="relative flex min-h-screen items-center justify-center bg-ink px-5">
      <div className="mashrabiya absolute inset-0 opacity-[0.05]" aria-hidden="true" />
      <form onSubmit={submit} className="glass relative w-full max-w-md space-y-5 rounded-sm p-8">
        <div className="flex items-center justify-between"><Logo /><LangToggle /></div>
        <h1 className="h-display text-3xl">{a("Admin Portal", "بوابة المشرفين")}</h1>
        <input data-testid="admin-login-email" type="email" required autoComplete="username" placeholder={a("Email", "البريد الإلكتروني")} className="field" dir="ltr" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <input data-testid="admin-login-password" type="password" required autoComplete="current-password" placeholder={a("Password", "كلمة المرور")} className="field" dir="ltr" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        {err && <p data-testid="admin-login-error" role="alert" className="text-sm text-[#E4472B]">{err}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-submit" className="btn-gold w-full">{busy ? "…" : a("Sign in", "تسجيل الدخول")}</button>
        <Link to="/" className="block text-center text-xs text-ivory/50 hover:text-gold">{a("Back to website", "العودة إلى الموقع")}</Link>
      </form>
    </div>
  );
}

function ChangePassword({ open, setOpen, forced = false }) {
  const a = useA();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error(a("Passwords do not match", "كلمتا المرور غير متطابقتين"));
    try { await aapi.post("/auth/change-password", f); toast.success(a("Password changed", "تم تغيير كلمة المرور")); if (forced) { window.location.reload(); return; } setOpen(false); setF({ current_password: "", new_password: "", confirm: "" }); } catch (ex) { toast.error(apiErr(ex)); }
  };
  return (
    <Dialog open={open} onOpenChange={forced ? () => {} : setOpen}>
      <DialogContent data-testid="change-password-dialog" className={`border-gold/20 bg-ink-2 text-ivory ${forced ? "[&>button.absolute]:hidden" : ""}`} onEscapeKeyDown={(e) => forced && e.preventDefault()} onInteractOutside={(e) => forced && e.preventDefault()}>
        <DialogTitle className="h-display text-2xl">{a("Change password", "تغيير كلمة المرور")}</DialogTitle>
        {forced && <p data-testid="forced-password-notice" className="text-sm text-gold">{a("For security, please set a new password before continuing.", "لأمانك، يرجى تعيين كلمة مرور جديدة قبل المتابعة.")}</p>}
        <form onSubmit={submit} className="space-y-3">
          {[["current_password", a("Current password", "كلمة المرور الحالية")], ["new_password", a("New password (min 10)", "كلمة مرور جديدة (١٠ أحرف على الأقل)")], ["confirm", a("Confirm new password", "تأكيد كلمة المرور")]].map(([k, l]) => (
            <input key={k} data-testid={`change-password-${k}`} type="password" required minLength={k === "current_password" ? 1 : 10} placeholder={l} className="field" dir="ltr" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          ))}
          <button type="submit" data-testid="change-password-submit" className="btn-gold w-full">{a("Update", "تحديث")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BellMenu() {
  const a = useA();
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const { data, reload } = useAdminApi("/admin/notifications");
  const nav = useNavigate();
  const toggle = async () => { setOpen(!open); if (!open && data?.unread) { await aapi.post("/admin/notifications/read"); reload(); } };
  return (
    <div className="relative">
      <button type="button" data-testid="admin-notifications-bell" onClick={toggle} aria-label={a("Notifications", "الإشعارات")} className="relative rounded-full p-2 text-ivory/80 hover:text-gold">
        <Bell className="h-5 w-5" />
        {data?.unread > 0 && <span data-testid="admin-notifications-unread" className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#E4472B] px-1 text-[10px] font-bold text-white">{data.unread}</span>}
      </button>
      {open && (
        <div data-testid="admin-notifications-panel" className="absolute end-0 top-11 z-50 w-80 rounded-sm border border-gold/20 bg-ink-2 p-2 shadow-2xl">
          {!data?.items.length ? <p className="p-4 text-sm text-ivory/50">{a("No notifications", "لا توجد إشعارات")}</p> : data.items.map((n) => (
            <button key={n.id} type="button" onClick={() => { setOpen(false); nav(n.link); }} className="block w-full rounded-sm p-3 text-start text-sm hover:bg-emerald/40">
              <span className="block text-ivory">{n.title}</span><span className="text-xs text-ivory/45">{fmtDT(n.created_at, lang)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Shell() {
  const a = useA();
  const { user, logout } = useAuth();
  const [pw, setPw] = useState(false);
  const [np, setNp] = useState(false);
  const [mob, setMob] = useState(false);
  const { isRtl } = useI18n();
  const items = NAV.filter((n) => can(user, n[4]));
  const side = (
    <nav className="flex flex-col gap-0.5 p-3" aria-label="Admin">
      {items.map(([k, Icon, en, ar]) => (
        <NavLink key={k} to={`/admin/${k}`} onClick={() => setMob(false)} data-testid={`admin-nav-${k}`} className={({ isActive }) => `flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-emerald text-gold" : "text-ivory/70 hover:bg-ivory/5 hover:text-ivory"}`}>
          <Icon className="h-4 w-4" />{a(en, ar)}
        </NavLink>
      ))}
    </nav>
  );
  return (
    <div className="flex min-h-screen bg-ink" data-testid="admin-shell">
      <aside className="hidden w-64 shrink-0 border-e border-gold/10 bg-ink-2 lg:block">
        <Link to="/admin" className="flex items-center gap-3 border-b border-gold/10 p-5"><Logo /><span className="font-display text-lg">Landmark CMS</span></Link>
        {side}
      </aside>
      {mob && <div className="fixed inset-0 z-50 flex lg:hidden"><div className="w-72 overflow-y-auto bg-ink-2">{side}</div><button type="button" aria-label="Close" className="flex-1 bg-black/60" onClick={() => setMob(false)} /></div>}
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-40 flex h-16 items-center justify-between gap-3 border-b border-gold/10 bg-ink/90 px-4 backdrop-blur-xl md:px-8">
          <button type="button" className="p-2 lg:hidden" onClick={() => setMob(true)} data-testid="admin-mobile-menu" aria-label="Menu"><Menu className="h-5 w-5" /></button>
          <p className="truncate text-sm text-ivory/60"><span data-testid="admin-user-name" className="text-ivory">{user.name}</span> · <span data-testid="admin-user-role" className="text-gold">{a(...ROLE_LABEL[user.role])}</span></p>
          <div className="flex items-center gap-2">
            <BellMenu />
            <button type="button" data-testid="admin-notify-prefs" onClick={() => setNp(true)} aria-label={a("Notification settings", "إعدادات الإشعارات")} className="rounded-full p-2 text-ivory/70 hover:text-gold"><SlidersHorizontal className="h-4 w-4" /></button>
            <LangToggle />
            <Link to="/" target="_blank" className="hidden text-xs text-ivory/60 hover:text-gold sm:block">{a("View site", "عرض الموقع")}</Link>
            <button type="button" data-testid="admin-change-password" onClick={() => setPw(true)} aria-label={a("Change password", "تغيير كلمة المرور")} className="rounded-full p-2 text-ivory/70 hover:text-gold"><KeyRound className="h-4 w-4" /></button>
            <button type="button" data-testid="admin-logout" onClick={logout} aria-label={a("Log out", "تسجيل الخروج")} className="rounded-full p-2 text-ivory/70 hover:text-gold"><LogOut className="rtl-flip h-4 w-4" /></button>
          </div>
        </header>
        <main className="p-4 md:p-8"><ErrorBoundary key={window.location.pathname}><Outlet /></ErrorBoundary></main>
      </div>
      <ChangePassword open={pw || !!user?.must_change_password} setOpen={setPw} forced={!!user?.must_change_password} />
      <NotifyPrefs open={np} setOpen={setNp} />
      <Toaster theme="dark" position={isRtl ? "top-left" : "top-right"} richColors />
    </div>
  );
}

function Guard({ roles, children }) {
  const { user } = useAuth();
  const a = useA();
  if (!can(user, roles)) return <p data-testid="admin-forbidden" className="glass rounded-sm p-8 text-ivory/60">{a("You do not have permission to view this page.", "ليست لديك صلاحية لعرض هذه الصفحة.")}</p>;
  return children;
}

function Home() {
  const { user } = useAuth();
  const first = NAV.find((n) => can(user, n[4]));
  return <Navigate to={`/admin/${first[0]}`} replace />;
}

function Protected() {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-ink text-ivory/50">…</div>;
  if (!user) return <Navigate to="/admin/login" replace state={{ from: loc.pathname }} />;
  return <Shell />;
}

const RES = ["properties", "projects", "articles", "banners", "testimonials", "partners", "pages", "categories", "locations", "agents"];

export default function AdminApp() {
  useEffect(() => applyTheme("champagne", "dark"), []);
  return (
    <AuthProvider>
      <Routes>
        <Route path="login" element={<LoginPage />} />
        <Route element={<Protected />}>
          <Route index element={<Home />} />
          <Route path="dashboard" element={<Guard roles={[SA, MG]}><Dashboard /></Guard>} />
          <Route path="leads" element={<Guard roles={[SA, MG, AG]}><Leads /></Guard>} />
          <Route path="appointments" element={<Guard roles={[SA, MG, AG]}><Appointments /></Guard>} />
          {RES.map((r) => {
            const roles = NAV.find((n) => n[0] === r)[4];
            return [
              <Route key={r} path={r} element={<Guard roles={roles}><ResourceList name={r} /></Guard>} />,
              <Route key={`${r}-e`} path={`${r}/:id`} element={<Guard roles={roles}><ResourceEdit name={r} /></Guard>} />,
            ];
          })}
          <Route path="stats" element={<Guard roles={CONTENT}><StatsPage /></Guard>} />
          <Route path="homepage" element={<Guard roles={CONTENT}><HomepagePage /></Guard>} />
          <Route path="settings" element={<Guard roles={[SA]}><SettingsPage /></Guard>} />
          <Route path="users" element={<Guard roles={[SA]}><UsersPage /></Guard>} />
          <Route path="activity" element={<Guard roles={[SA]}><ActivityPage /></Guard>} />
          <Route path="backups" element={<Guard roles={[SA]}><BackupsPage /></Guard>} />
          <Route path="email-log" element={<Guard roles={[SA, MG]}><EmailLogPage /></Guard>} />
          <Route path="chats" element={<Guard roles={[SA, MG]}><ChatsPage /></Guard>} />
          <Route path="docs" element={<Guard roles={[SA]}><DocsPage /></Guard>} />
          <Route path="integrations" element={<Guard roles={[SA, MG]}><IntegrationsPage /></Guard>} />
          <Route path="*" element={<Home />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}
